"use client";

import React, { useState, useEffect } from "react";
import {
  DownloadCloudIcon,
  Edit,
  Plus,
  Trash2,
  TrashIcon,
  UploadIcon,
  X,
} from "lucide-react";
import BookingTable from "../common/Table";
import Button from "./Button";
import { 
  useAddCoupanType,
  useUpdateCouponType,
  useGetAllCoupanTypes
} from "@/hooks/useCommon";
import { useAuth } from "@/store/auth";

interface CouponType {
  id: number;
  name: string;
  valid_from_date: string | null;
  valid_to_date: string | null;
}

const CouponTypeMaster: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCouponType, setCurrentCouponType] = useState<CouponType | null>(null);
  const [couponTypes, setCouponTypes] = useState<CouponType[]>([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  
  // Form states
  const [name, setName] = useState<string>("");
  const [validFromDate, setValidFromDate] = useState<string>("");
  const [validToDate, setValidToDate] = useState<string>("");

  // API Hooks
  const getCouponTypes = useGetAllCoupanTypes();
  const addCouponType = useAddCoupanType();
  const updateCouponType = useUpdateCouponType();
  
  const user = useAuth();
  const loggedInUser = user?.user?.id;

  // Fetch coupon types on component mount
  useEffect(() => {
    if (getCouponTypes.data?.success) {
      setCouponTypes(getCouponTypes.data.data);
    }
  }, [getCouponTypes.data]);

  // Reset states when modal opens/closes
  useEffect(() => {
    if (!isModalOpen) {
      setCurrentCouponType(null);
      setName("");
      setValidFromDate("");
      setValidToDate("");
    }
  }, [isModalOpen]);

  // Set form data when currentCouponType changes (for editing)
  useEffect(() => {
    if (currentCouponType && isModalOpen) {
      setName(currentCouponType.name || "");
      setValidFromDate(currentCouponType.valid_from_date ? formatDateForInput(currentCouponType.valid_from_date) : "");
      setValidToDate(currentCouponType.valid_to_date ? formatDateForInput(currentCouponType.valid_to_date) : "");
    }
  }, [currentCouponType, isModalOpen]);

  // Format date for input field (YYYY-MM-DD)
  const formatDateForInput = (dateString: string) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Format date for display
  const formatDateForDisplay = (dateString: string | null) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!name.trim()) {
      alert("Please enter a coupon type name");
      return;
    }

    try {
      const payload = {
        name: name.trim(),
        valid_from_date: validFromDate || null,
        valid_to_date: validToDate || null,
       
        created_by: loggedInUser,
        modified_by: loggedInUser,
      };

      if (currentCouponType) {
        // Update existing coupon type
        updateCouponType.mutate(
          {
            ...payload,
            auto_id: currentCouponType.id,
          },
          {
            onSuccess: (data) => {
              if (data.success) {
                getCouponTypes.refetch();
                setIsModalOpen(false);
                setCurrentCouponType(null);
              }
            },
            onError: (error) => {
              alert("Failed to update coupon type. Please try again.");
            },
          }
        );
      } else {
        // Add new coupon type
        addCouponType.mutate(
          payload,
          {
            onSuccess: (data) => {
              if (data.success) {
                getCouponTypes.refetch();
                setIsModalOpen(false);
              }
            },
            onError: (error) => {
              alert("Failed to add coupon type. Please try again.");
            },
          }
        );
      }
    } catch (error) {
      alert("An error occurred. Please try again.");
    }
  };

  const handlePublish = () => {
    if (selectedRows.length === 0) return;
    // Implement publish logic if needed
    console.log("Publish selected:", selectedRows);
  };

  const handleUnpublish = () => {
    if (selectedRows.length === 0) return;
    // Implement unpublish logic if needed
    console.log("Unpublish selected:", selectedRows);
  };

  const handleDelete = () => {
    if (selectedRows.length === 0) return;

    if (confirm("Are you sure you want to delete selected coupon types?")) {
      // Note: You might need to implement a delete mutation if available
      // For now, we'll just clear selection
      setSelectedRows([]);
    }
  };

  // Transform API data to match table structure
  const tableData = couponTypes.map((item, index) => ({
    sno: index + 1,
    id: item.id,
    name: item.name,
    validFromDate: item.valid_from_date,
    validToDate: item.valid_to_date,
    originalData: item,
  }));

  const columns = [
    {
      key: "selection",
      header: "",
      sortable: false,
      render: (_: any, row: any) => (
        <input
          type="checkbox"
          checked={selectedRows.includes(row.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows([...selectedRows, row.id]);
            } else {
              setSelectedRows(selectedRows.filter((id) => id !== row.id));
            }
          }}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      ),
    },
    { key: "sno", header: "S.No", sortable: true },
    { key: "name", header: "Coupon Type Name", sortable: true },
    {
      key: "validFromDate",
      header: "Valid From",
      sortable: true,
      render: (date: string | null) => formatDateForDisplay(date),
    },
    {
      key: "validToDate",
      header: "Valid To",
      sortable: true,
      render: (date: string | null) => formatDateForDisplay(date),
    },
    {
      key: "action",
      header: "Action",
      render: (_: any, row: any) => {
        return (
          <div className="flex space-x-2">
            <button
              className="text-blue-500 hover:text-blue-700"
              onClick={() => {
                setCurrentCouponType(row.originalData);
                setIsModalOpen(true);
              }}
            >
              <Edit size={15} />
            </button>
            <button
              className="text-red-500 hover:text-red-700"
              onClick={() => {
                if (confirm("Are you sure you want to delete this coupon type?")) {
                  setSelectedRows([row.id]);
                  handleDelete();
                }
              }}
            >
              <Trash2 size={15} />
            </button>
          </div>
        );
      },
    },
  ];

  // Select all checkbox handler
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedRows(tableData.map((row) => row.id));
    } else {
      setSelectedRows([]);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
      <div className="mt-5">
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            onClick={() => {
              setCurrentCouponType(null);
              setIsModalOpen(true);
            }}
            icon={<Plus className="w-4 h-4" />}
            iconPosition="left"
          >
            Add Coupon Type
          </Button>

          <Button
            size="sm"
            onClick={handlePublish}
            icon={<UploadIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0}
          >
            Publish
          </Button>

          <Button
            size="sm"
            onClick={handleUnpublish}
            icon={<DownloadCloudIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0}
          >
            Unpublish
          </Button>

          <Button
            size="sm"
            variant="danger"
            onClick={handleDelete}
            icon={<TrashIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0}
          >
            Delete
          </Button>
        </div>

        <BookingTable
          data={tableData}
          columns={columns}
          heading={[]}
          searchable={true}
          filterable={true}
          sortable={true}
          selectable={true}
          onSelectionChange={setSelectedRows}
          loading={getCouponTypes.isLoading}
          renderHeader={() => (
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === tableData.length &&
                      tableData.length > 0
                    }
                    onChange={handleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  S.No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Coupon Type Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valid From
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valid To
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
          )}
        />
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => {
              setIsModalOpen(false);
              setCurrentCouponType(null);
            }}
          />

          <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
            <div
              className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-md mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                  {currentCouponType
                    ? "Update Coupon Type"
                    : "Add Coupon Type"}
                </h2>

                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setCurrentCouponType(null);
                  }}
                  className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
                  aria-label="Close modal"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                </button>
              </div>

              <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
                <form onSubmit={handleFormSubmit} className="space-y-4">
                  {/* Name */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Coupon Type Name *
                    </label>
                    <input
                      type="text"
                      value={name || ""}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter coupon type name"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Valid From Date */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Valid From Date
                    </label>
                    <input
                      type="date"
                      value={validFromDate || ""}
                      onChange={(e) => setValidFromDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Valid To Date */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Valid To Date
                    </label>
                    <input
                      type="date"
                      value={validToDate || ""}
                      onChange={(e) => setValidToDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end pt-4">
                    <button
                      type="submit"
                      disabled={
                        addCouponType.isPending || updateCouponType.isPending
                      }
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {addCouponType.isPending || updateCouponType.isPending
                        ? "Processing..."
                        : currentCouponType
                        ? "Update Coupon Type"
                        : "Add Coupon Type"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponTypeMaster;